import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { format } from 'date-fns';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';
import { isUsingFallbackClient } from '../lib/supabase';
import journalPromptsData from '../data/journalPrompts.json';

export interface JournalEntry {
  id: string;
  title: string;
  content: string;
  mood: number;
  anxiety: number;
  energy: number;
  sleepQuality: number;
  gratitude: string[];
  tags: string[];
  triggers: string[];
  privacyLevel: 'private' | 'shared' | 'anonymous';
  createdAt: Date;
  updatedAt: Date;
  wordCount: number;
  writingTime: number; // in seconds
  voiceNotes?: string[];
  isVoiceEntry: boolean;
  autoSaved: boolean;
  audioUrl?: string | null; // URL to stored audio file
  voiceAnalytics?: any;
}

interface JournalStats {
  totalEntries: number;
  averageMood: number;
  streakDays: number;
  totalWords: number;
  mostUsedTags: string[];
  moodTrend: number[];
}

interface JournalSummary {
  id: string;
  userId: string;
  summaryDate: Date;
  entryCount: number;
  avgMood: number;
  avgAnxiety: number;
  avgEnergy: number;
  avgSleepQuality: number;
  totalWords: number;
  totalWritingTime: number;
  tags: string[];
}

interface JournalContextType {
  entries: JournalEntry[];
  currentEntry: JournalEntry | null;
  stats: JournalStats;
  createEntry: () => JournalEntry;
  saveEntry: (entry: JournalEntry) => Promise<void>;
  deleteEntry: (id: string) => Promise<void>;
  getEntry: (id: string) => JournalEntry | undefined;
  updateEntry: (id: string, updates: Partial<JournalEntry>) => Promise<void>;
  autoSaveEntry: (id: string, updates: Partial<JournalEntry>) => void;
  getPrompts: () => string[];
  getDailySummaries: (startDate: Date, endDate: Date) => Promise<JournalSummary[]>;
  isLoading: boolean;
  error: string | null;
}

const JournalContext = createContext<JournalContextType | undefined>(undefined);

export const useJournal = () => {
  const context = useContext(JournalContext);
  if (context === undefined) {
    throw new Error('useJournal must be used within a JournalProvider');
  }
  return context;
};

// Import CBT prompts from external JSON
const CBT_PROMPTS = journalPromptsData.cbtPrompts;

export const JournalProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, isLoading: authLoading } = useAuth();
  const [entries, setEntries] = useState<JournalEntry[]>([]);
  const [currentEntry, setCurrentEntry] = useState<JournalEntry | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load entries when user changes, but only after auth has finished loading
  useEffect(() => {
    // Don't load entries if auth is still loading
    if (authLoading) return;
    
    if (user) {
      loadEntries();
    } else {
      setEntries([]);
    }
  }, [user, authLoading]);

  const loadEntries = async () => {
    if (!user) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Check if we're using the fallback client (demo mode)
      if (isUsingFallbackClient()) {
        console.log('Running in demo mode - using localStorage for journal entries');
        // In demo mode, try to load from localStorage
        const savedEntries = localStorage.getItem(`journal_entries_${user.id}`);
        if (savedEntries) {
          const parsedEntries = JSON.parse(savedEntries).map((entry: any) => ({
            ...entry,
            createdAt: new Date(entry.createdAt),
            updatedAt: new Date(entry.updatedAt)
          }));
          setEntries(parsedEntries);
        } else {
          setEntries([]);
        }
        setIsLoading(false);
        return;
      }
      
      // Validate that user.id is a valid UUID before querying Supabase
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      if (!uuidRegex.test(user.id)) {
        console.warn('Invalid UUID format for user ID, skipping Supabase query:', user.id);
        setEntries([]);
        setIsLoading(false);
        return;
      }
      
      // Only query Supabase for real users with valid UUIDs
      const { data, error } = await supabase
        .from('journal_entries')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      // Transform database entries to JournalEntry format
      const transformedEntries: JournalEntry[] = data.map(entry => ({
        id: entry.id,
        title: entry.title || '',
        content: entry.content || '',
        mood: entry.mood,
        anxiety: entry.anxiety,
        energy: entry.energy,
        sleepQuality: entry.sleep_quality,
        gratitude: entry.gratitude || [],
        tags: entry.tags || [],
        triggers: entry.triggers || [],
        privacyLevel: entry.privacy_level,
        createdAt: new Date(entry.created_at),
        updatedAt: new Date(entry.updated_at),
        wordCount: entry.word_count,
        writingTime: entry.writing_time,
        isVoiceEntry: entry.is_voice_entry,
        autoSaved: false,
        audioUrl: entry.voice_analytics?.audioUrl || null,
        voiceAnalytics: entry.voice_analytics
      }));
      
      setEntries(transformedEntries);
    } catch (err) {
      console.error('Error loading journal entries:', err);
      
      // Check if it's a network error
      if (err instanceof TypeError && err.message.includes('fetch')) {
        setError('Unable to connect to the server. Please check your internet connection and try again.');
      } else {
        setError('Failed to load journal entries. Please try again later.');
      }
      
      // Fallback to localStorage in case of network issues
      try {
        const savedEntries = localStorage.getItem(`journal_entries_${user.id}`);
        if (savedEntries) {
          const parsedEntries = JSON.parse(savedEntries).map((entry: any) => ({
            ...entry,
            createdAt: new Date(entry.createdAt),
            updatedAt: new Date(entry.updatedAt)
          }));
          setEntries(parsedEntries);
          setError('Loaded offline entries. Some features may be limited.');
        }
      } catch (localStorageError) {
        console.error('Failed to load from localStorage:', localStorageError);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const createEntry = useCallback((): JournalEntry => {
    const newEntry: JournalEntry = {
      id: crypto.randomUUID(),
      title: '',
      content: '',
      mood: 5,
      anxiety: 5,
      energy: 5,
      sleepQuality: 5,
      gratitude: [],
      tags: [],
      triggers: [],
      privacyLevel: 'private',
      createdAt: new Date(),
      updatedAt: new Date(),
      wordCount: 0,
      writingTime: 0,
      isVoiceEntry: false,
      autoSaved: false
    };
    
    setCurrentEntry(newEntry);
    return newEntry;
  }, []);

  const saveEntry = async (entry: JournalEntry): Promise<void> => {
    if (!user) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Check if we're using the fallback client (demo mode)
      if (isUsingFallbackClient()) {
        console.log('Running in demo mode - saving to localStorage');
        // In demo mode, save to localStorage
        const existingIndex = entries.findIndex(e => e.id === entry.id);
        let updatedEntries;
        
        const updatedEntry = {
          ...entry,
          updatedAt: new Date(),
          autoSaved: false
        };
        
        if (existingIndex >= 0) {
          updatedEntries = [...entries];
          updatedEntries[existingIndex] = updatedEntry;
        } else {
          updatedEntries = [updatedEntry, ...entries];
        }
        
        // Save to localStorage
        localStorage.setItem(`journal_entries_${user.id}`, JSON.stringify(updatedEntries));
        setEntries(updatedEntries);
        setCurrentEntry(updatedEntry);
        setIsLoading(false);
        return;
      }
      
      // Validate UUID before attempting Supabase operations
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      if (!uuidRegex.test(user.id)) {
        console.warn('Invalid UUID format for user ID, skipping Supabase save:', user.id);
        return;
      }
      
      // Extract audioUrl from entry if it exists
      const { audioUrl, ...entryWithoutAudioUrl } = entry;
      
      // Transform entry to database format
      const dbEntry = {
        id: entry.id,
        user_id: user.id,
        title: entry.title,
        content: entry.content,
        mood: entry.mood,
        anxiety: entry.anxiety,
        energy: entry.energy,
        sleep_quality: entry.sleepQuality,
        gratitude: entry.gratitude,
        tags: entry.tags,
        triggers: entry.triggers,
        privacy_level: entry.privacyLevel,
        word_count: entry.wordCount,
        writing_time: entry.writingTime,
        is_voice_entry: entry.isVoiceEntry,
        voice_analytics: entry.isVoiceEntry ? { 
          ...entry.voiceAnalytics,
          audioUrl: audioUrl // Store the audio URL in the voice_analytics JSON field
        } : null
      };
      
      // Check if entry exists
      const { data: existingEntry } = await supabase
        .from('journal_entries')
        .select('id')
        .eq('id', entry.id)
        .maybeSingle();
      
      if (existingEntry) {
        // Update existing entry
        const { error } = await supabase
          .from('journal_entries')
          .update(dbEntry)
          .eq('id', entry.id);
        
        if (error) throw error;
      } else {
        // Insert new entry
        const { error } = await supabase
          .from('journal_entries')
          .insert([dbEntry]);
        
        if (error) throw error;
      }
      
      // Update local state
      const updatedEntry = {
        ...entry,
        updatedAt: new Date(),
        autoSaved: false
      };
      
      const existingIndex = entries.findIndex(e => e.id === entry.id);
      let updatedEntries;
      
      if (existingIndex >= 0) {
        updatedEntries = [...entries];
        updatedEntries[existingIndex] = updatedEntry;
      } else {
        updatedEntries = [updatedEntry, ...entries];
      }
      
      setEntries(updatedEntries);
      setCurrentEntry(updatedEntry);
      
      // Also save to localStorage as backup
      try {
        localStorage.setItem(`journal_entries_${user.id}`, JSON.stringify(updatedEntries));
      } catch (localStorageError) {
        console.warn('Failed to save to localStorage:', localStorageError);
      }
      
    } catch (err) {
      console.error('Error saving journal entry:', err);
      
      // Check if it's a network error
      if (err instanceof TypeError && err.message.includes('fetch')) {
        setError('Unable to save to server. Entry saved locally.');
        
        // Save to localStorage as fallback
        try {
          const existingIndex = entries.findIndex(e => e.id === entry.id);
          let updatedEntries;
          
          const updatedEntry = {
            ...entry,
            updatedAt: new Date(),
            autoSaved: false
          };
          
          if (existingIndex >= 0) {
            updatedEntries = [...entries];
            updatedEntries[existingIndex] = updatedEntry;
          } else {
            updatedEntries = [updatedEntry, ...entries];
          }
          
          localStorage.setItem(`journal_entries_${user.id}`, JSON.stringify(updatedEntries));
          setEntries(updatedEntries);
          setCurrentEntry(updatedEntry);
        } catch (localStorageError) {
          setError('Failed to save journal entry');
          throw err;
        }
      } else {
        setError('Failed to save journal entry');
        throw err;
      }
    } finally {
      setIsLoading(false);
    }
  };

  const autoSaveEntry = (id: string, updates: Partial<JournalEntry>) => {
    // Only update local state for auto-save
    // This will be saved to the database when the user explicitly saves
    const existingIndex = entries.findIndex(e => e.id === id);
    
    const updatedEntry = {
      ...updates,
      id,
      updatedAt: new Date(),
      autoSaved: true
    } as JournalEntry;
    
    let updatedEntries;
    
    if (existingIndex >= 0) {
      updatedEntries = [...entries];
      updatedEntries[existingIndex] = { ...updatedEntries[existingIndex], ...updatedEntry };
    } else {
      updatedEntries = [updatedEntry, ...entries];
    }
    
    setEntries(updatedEntries);
    // Removed setCurrentEntry call to prevent conflicts with local state in JournalEntry component
  };

  const deleteEntry = async (id: string): Promise<void> => {
    if (!user) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Get the entry to check if it has an audio file
      const entry = entries.find(e => e.id === id);
      
      // Check if we're using the fallback client (demo mode)
      if (isUsingFallbackClient()) {
        console.log('Running in demo mode - deleting from localStorage');
        const updatedEntries = entries.filter(entry => entry.id !== id);
        setEntries(updatedEntries);
        localStorage.setItem(`journal_entries_${user.id}`, JSON.stringify(updatedEntries));
        
        if (currentEntry?.id === id) {
          setCurrentEntry(null);
        }
        setIsLoading(false);
        return;
      }
      
      // Validate UUID before attempting Supabase operations
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      if (!uuidRegex.test(user.id)) {
        console.warn('Invalid UUID format for user ID, skipping Supabase delete:', user.id);
        return;
      }
      
      // Delete the entry from the database
      const { error } = await supabase
        .from('journal_entries')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      // If the entry has an audio file, delete it from storage
      if (entry?.audioUrl) {
        try {
          // Extract the path from the URL
          const url = new URL(entry.audioUrl);
          const path = url.pathname.split('/').slice(2).join('/'); // Remove /storage/v1/object/public/
          
          // Delete the file from storage
          const { error: storageError } = await supabase.storage
            .from('media')
            .remove([path]);
          
          if (storageError) {
            console.error('Failed to delete audio file:', storageError);
            // Continue with entry deletion even if file deletion fails
          }
        } catch (urlError) {
          console.error('Failed to parse audio URL:', urlError);
        }
      }
      
      // Update local state
      const updatedEntries = entries.filter(entry => entry.id !== id);
      setEntries(updatedEntries);
      
      // Update localStorage backup
      try {
        localStorage.setItem(`journal_entries_${user.id}`, JSON.stringify(updatedEntries));
      } catch (localStorageError) {
        console.warn('Failed to update localStorage:', localStorageError);
      }
      
      if (currentEntry?.id === id) {
        setCurrentEntry(null);
      }
    } catch (err) {
      console.error('Error deleting journal entry:', err);
      
      if (err instanceof TypeError && err.message.includes('fetch')) {
        setError('Unable to connect to server. Please try again later.');
      } else {
        setError('Failed to delete journal entry');
      }
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const getEntry = useCallback((id: string): JournalEntry | undefined => {
    return entries.find(entry => entry.id === id);
  }, [entries]);

  const updateEntry = async (id: string, updates: Partial<JournalEntry>): Promise<void> => {
    const existingEntry = getEntry(id);
    if (!existingEntry) return;
    
    const updatedEntry = { ...existingEntry, ...updates };
    await saveEntry(updatedEntry);
  };

  const getPrompts = (): string[] => {
    return CBT_PROMPTS.sort(() => Math.random() - 0.5).slice(0, 3);
  };

  const getDailySummaries = async (startDate: Date, endDate: Date): Promise<JournalSummary[]> => {
    if (!user) return [];
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Check if we're using the fallback client (demo mode)
      if (isUsingFallbackClient()) {
        console.log('Running in demo mode - journal summaries not available');
        return [];
      }
      
      // Validate UUID before attempting Supabase operations
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      if (!uuidRegex.test(user.id)) {
        console.warn('Invalid UUID format for user ID, skipping Supabase query:', user.id);
        return [];
      }
      
      const { data, error } = await supabase
        .from('journal_summaries')
        .select('*')
        .eq('user_id', user.id)
        .gte('summary_date', startDate.toISOString().split('T')[0])
        .lte('summary_date', endDate.toISOString().split('T')[0])
        .order('summary_date', { ascending: false });
      
      if (error) throw error;
      
      // Transform database summaries to JournalSummary format
      return data.map(summary => ({
        id: summary.id,
        userId: summary.user_id,
        summaryDate: new Date(summary.summary_date),
        entryCount: summary.entry_count,
        avgMood: summary.avg_mood,
        avgAnxiety: summary.avg_anxiety,
        avgEnergy: summary.avg_energy,
        avgSleepQuality: summary.avg_sleep_quality,
        totalWords: summary.total_words,
        totalWritingTime: summary.total_writing_time,
        tags: summary.tags || []
      }));
    } catch (err) {
      console.error('Error loading journal summaries:', err);
      
      if (err instanceof TypeError && err.message.includes('fetch')) {
        setError('Unable to connect to server for summaries');
      } else {
        setError('Failed to load journal summaries');
      }
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const calculateStats = (): JournalStats => {
    if (entries.length === 0) {
      return {
        totalEntries: 0,
        averageMood: 0,
        streakDays: 0,
        totalWords: 0,
        mostUsedTags: [],
        moodTrend: []
      };
    }

    const totalWords = entries.reduce((sum, entry) => sum + entry.wordCount, 0);
    const averageMood = entries.reduce((sum, entry) => sum + entry.mood, 0) / entries.length;
    
    // Calculate streak
    const sortedEntries = [...entries].sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
    let streakDays = 0;
    let currentDate = new Date();
    
    for (const entry of sortedEntries) {
      const entryDate = format(entry.createdAt, 'yyyy-MM-dd');
      const checkDate = format(currentDate, 'yyyy-MM-dd');
      
      if (entryDate === checkDate) {
        streakDays++;
        currentDate.setDate(currentDate.getDate() - 1);
      } else {
        break;
      }
    }

    // Most used tags
    const tagCounts: Record<string, number> = {};
    entries.forEach(entry => {
      entry.tags.forEach(tag => {
        tagCounts[tag] = (tagCounts[tag] || 0) + 1;
      });
    });
    
    const mostUsedTags = Object.entries(tagCounts)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 5)
      .map(([tag]) => tag);

    // Mood trend (last 7 days)
    const last7Days = entries
      .filter(entry => {
        const daysDiff = (Date.now() - entry.createdAt.getTime()) / (1000 * 60 * 60 * 24);
        return daysDiff <= 7;
      })
      .sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime())
      .map(entry => entry.mood);

    return {
      totalEntries: entries.length,
      averageMood,
      streakDays,
      totalWords,
      mostUsedTags,
      moodTrend: last7Days
    };
  };

  const stats = calculateStats();

  const value = {
    entries,
    currentEntry,
    stats,
    createEntry,
    saveEntry,
    deleteEntry,
    getEntry,
    updateEntry,
    autoSaveEntry,
    getPrompts,
    getDailySummaries,
    isLoading,
    error
  };

  return <JournalContext.Provider value={value}>{children}</JournalContext.Provider>;
};